import axios from 'axios';
import { supabase } from './supabase';

export const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});
console.log('process env:', process.env);
// Add a request interceptor to add the auth token
api.interceptors.request.use(async (config) => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;

    if (session?.access_token) {
      config.headers.Authorization = `Bearer ${session.access_token}`;
    }

    // Transmet la boutique sélectionnée afin que les routes migrées vers shop_id puissent l'isoler.
    const activeShopId = window.localStorage.getItem('activeShopId');
    if (activeShopId) config.headers['X-Shop-Id'] = activeShopId;

    return config;
  } catch (error) {
    console.error('Error getting session:', error);
    return config;
  }
});

// Add a response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) throw refreshError;

        if (session?.access_token) {
          originalRequest.headers.Authorization = `Bearer ${session.access_token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error('Error refreshing session:', refreshError);
        // Redirect to login if session refresh fails
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// API endpoints
export const whatsappApi = {
  getStatus: () => api.get('/whatsapp/status'),
  getQR: () => api.get('/whatsapp/qr'),
  connect: () => api.post('/whatsapp/connect'),
  disconnect: () => api.post('/whatsapp/disconnect'),
  sendMessage: (data) => api.post('/whatsapp/send-message', data),
  sendAvailability: (data) => api.post('/whatsapp/send-availability', data),
  sendOrders: (data) => api.post('/whatsapp/send-orders', data),
  /**
   * Récupère les indicateurs de la file WhatsApp du compte connecté : messages
   * en attente, en cours, envoyés ou échoués, ainsi que le débit courant.
   */
  getQueueStats: () => api.get('/whatsapp/queue/stats'),
  /**
   * Récupère les messages qui attendent encore leur envoi WhatsApp. La limite
   * borne le volume retourné pour conserver un affichage rapide dans le tableau.
   * @param {number} limit Nombre maximal de messages à retourner.
   */
  getPendingQueueMessages: (limit = 50) => api.get('/whatsapp/queue/pending', { params: { limit } }),
  /**
   * Récupère l'historique récent des envois et des échecs WhatsApp du compte.
   * @param {number} hours Fenêtre temporelle, en heures, à consulter.
   * @param {number} limit Nombre maximal de lignes d'historique à retourner.
   */
  getQueueHistory: (hours = 24, limit = 50) => api.get('/whatsapp/queue/history', { params: { hours, limit } }),
  /**
   * Annule un message uniquement lorsqu'il est toujours en attente dans la file.
   * @param {string} messageId Identifiant du message de file à annuler.
   */
  cancelQueueMessage: (messageId) => api.delete(`/whatsapp/queue/${messageId}`),
  /** Récupère l'intégration API active sans jamais inclure sa clé secrète. */
  getIntegration: () => api.get('/whatsapp/integration'),
  /** Enregistre les identifiants d'un fournisseur WhatsApp côté serveur. */
  saveIntegration: (data) => api.put('/whatsapp/integration', data),
  /** Désactive l'intégration API afin de réutiliser la connexion directe. */
  disableIntegration: () => api.delete('/whatsapp/integration'),
};

export const settingsApi = {
  getGmailSettings: () => api.get('/settings/gmail'),
  updateGmailSettings: (data) => api.put('/settings/gmail', data),
  getGmailAuthUrl: () => api.get('/settings/gmail/auth-url'),
  /** Lit l'état masqué de la connexion Google Sheets Leads du compte connecté. */
  getGoogleSheetsLeadsSettings: () => api.get('/settings/google-sheets/leads'),
  /** Envoie les identifiants Google au serveur, qui les chiffre avant stockage. */
  updateGoogleSheetsLeadsSettings: (data) => api.put('/settings/google-sheets/leads', data),
  /** Demande l'URL sécurisée d'autorisation OAuth pour le compte Google de l'utilisateur. */
  getGoogleSheetsLeadsOAuthUrl: () => api.get('/settings/google-sheets/leads/oauth-url'),
  /** Vérifie l'accès au fichier Leads sans modifier son contenu. */
  testGoogleSheetsLeadsConnection: () => api.post('/settings/google-sheets/leads/test'),
};

export const emailApi = {
  syncEmails: () => api.post('/email/sync'),
  getEmails: () => api.get('/email/list')
};

export const conversationsApi = {
  getAll: () => api.get('/conversations'),
  getById: (id) => api.get(`/conversations/${id}`),
  create: (data) => api.post('/conversations', data),
  update: (id, data) => api.put(`/conversations/${id}`, data),
  delete: (id) => api.delete(`/conversations/${id}`),
};

export const messagesApi = {
  getByConversation: (conversationId) => api.get(`/messages/${conversationId}`),
  send: (conversationId, data) => api.post(`/messages/${conversationId}`, data),
};

export const profileApi = {
  get: () => api.get('/profile'),
  update: (data) => api.put('/profile', data),
  updateSettings: (data) => api.put('/profile/settings', data),
};

export const analyticsApi = {
  getStats: () => api.get('/analytics/stats'),
  getMessageHistory: (params) => api.get('/analytics/messages', { params }),
  getConversationMetrics: () => api.get('/analytics/conversations'),
  /** Récupère toutes les données agrégées pour le tableau de bord */
  getDashboard: () => api.get('/analytics/dashboard'),
  /** Récupère les métriques de rentabilité (revenus, dépenses, profit net, marge) */
  getProfitability: (params) => api.get('/analytics/profitability', { params }),
  /** Récupère l'évolution du profit dans le temps */
  getProfitEvolution: (params) => api.get('/analytics/profit-evolution', { params }),
  /** Récupère les providers IA disponibles sur le serveur (claude, gpt4) */
  getAIProviders: () => api.get('/analytics/ai-providers'),
  /** Demande une analyse IA de la rentabilité avec recommandations
   * @param {Object} params - { provider: 'claude'|'gpt4', start_date, end_date }
   */
  getAIAnalysis: (params) => api.post('/analytics/ai-analysis', params),
};

// Nouvelle API pour les boutiques
export const shopsApi = {
  /** Récupère toutes les boutiques (filtrées par RLS côté serveur) */
  getAll: (filters = {}) => api.get('/shops', { params: filters }),
  /** Récupère une boutique par son ID */
  getById: (id) => api.get(`/shops/${id}`),
  /** Crée une nouvelle boutique 
   * @param {Object} data - { name, address?, phone?, website? } 
   */
  create: (data) => api.post('/shops', data),
  /** Met à jour une boutique 
   * @param {string} id - ID de la boutique
   * @param {Object} data - { name?, address?, phone?, website?, is_active? }
   */
  update: (id, data) => api.put(`/shops/${id}`, data),
  /** Supprime une boutique */
  delete: (id) => api.delete(`/shops/${id}`),
};

// API de gestion de l'équipe d'une boutique : accès et invitations contrôlés côté serveur.
export const shopTeamApi = {
  /** Récupère les membres et invitations d'une boutique. */
  get: (shopId) => api.get(`/shops/${shopId}/team`),
  /** Envoie une invitation au rôle demandé. */
  invite: (shopId, data) => api.post(`/shops/${shopId}/team/invitations`, data),
  /** Met à jour le rôle d'un membre. */
  updateMemberRole: (shopId, memberId, role) => api.patch(`/shops/${shopId}/team/members/${memberId}`, { role }),
  /** Retire un membre de la boutique. */
  removeMember: (shopId, memberId) => api.delete(`/shops/${shopId}/team/members/${memberId}`),
  /** Annule une invitation en attente. */
  cancelInvitation: (shopId, invitationId) => api.delete(`/shops/${shopId}/team/invitations/${invitationId}`),
  /** Accepte une invitation avec le compte actuellement connecté. */
  acceptInvitation: (token) => api.post(`/shops/team-invitations/${token}/accept`),
};

// API des points de vente physiques de la boutique e-commerce active.
export const locationsApi = {
  /** Récupère les points de vente du contexte boutique sélectionné. */
  getAll: (filters = {}) => api.get('/locations', { params: filters }),
  /** Crée un point de vente physique et génère son code POS côté serveur. */
  create: (data) => api.post('/locations', data),
  /** Met à jour le nom, les coordonnées ou le statut d'un point de vente. */
  update: (id, data) => api.put(`/locations/${id}`, data),
  /** Supprime un point de vente sans caissier assigné. */
  delete: (id) => api.delete(`/locations/${id}`),
};

export const cashiersApi = {
  /** Récupère tous les caissiers (avec location_id et location_name). */
  getAll: () => api.get('/cashiers'),
  // getById n'est probablement pas utilisé mais on le laisse au cas où
  getById: (id) => api.get(`/cashiers/${id}`),
  /** Crée un nouveau caissier 
   * @param {Object} data - { name, pinCode, locationId }
   */
  create: (data) => api.post('/cashiers', data),
  /** Met à jour un caissier 
   * @param {string} id - ID du caissier
   * @param {Object} data - { name?, pinCode?, is_active?, locationId? }
   */
  update: (id, data) => api.patch(`/cashiers/${id}`, data),
  /** Authentifie un caissier par PIN 
   * @param {string} pinCode
   * @returns {Promise<AxiosResponse<{ cashier: Object, token: string }>>} - Infos caissier et point de vente POS.
   */
  login: (pinCode) => api.post('/cashiers/login', { pin_code: pinCode }),
  // delete n'existe pas côté serveur, on le commente ou retire
  // delete: (id) => api.delete(`/cashiers/${id}`)
};

export const integrationsApi = {
  testShopifyConnection: (shop_name, api_key, api_password, api_version) => api.post('/integrations/shopify/test-connection', { shop_name, api_key, api_password, api_version }),
  previewShopifyOrders: (credentials, filters) => api.post('/integrations/shopify/preview-orders', { credentials, filters }),
  checkDuplicateOrders: (orderIds) => api.post('/integrations/orders/check-duplicates', { orderIds }),
  importShopifyOrders: (credentials, filteredOrders) => api.post('/integrations/shopify/import-orders', { credentials, filteredOrders }),
  validateShopifyCredentials: (shopDomain, accessToken) => api.post('/integrations/shopify/validate', { shopDomain, accessToken }),
  fetchShopifyProducts: (shopDomain, accessToken, cursor = null) => api.post('/integrations/shopify/products', { shopDomain, accessToken, cursor }),
  importShopifyProducts: (shopDomain, accessToken, productIds) => api.post('/integrations/shopify/import', { shopDomain, accessToken, productIds }),
  fetchShopifyProductDetails: (productId, shopDomain, accessToken) => api.post(`/integrations/shopify/products/${productId}`, { shopDomain, accessToken }),
  /**
   * Lance l'importation des clients depuis Shopify.
   * @returns {Promise<AxiosResponse<any>>} La réponse axios.
   */
  importShopifyCustomers: () => api.post('/integrations/shopify/import-customers'),
};

// API pour le site e-commerce (lecture des produits et création de commandes)
export const siteApi = {
  /**
   * Génère une clé API pour que le site web puisse accéder aux données
   * @param {string} siteDomain - Domaine du site (ex: monsite.com)
   * @param {string} siteName - Nom optionnel du site
   * @returns {Promise<AxiosResponse>} Réponse avec apiKey et informations
   */
  generateApiKey: (siteDomain, siteName = null) =>
    api.post('/site/generate-api-key', { siteDomain, siteName }),

  /**
   * Récupère les informations de l'API site de l'utilisateur
   * @returns {Promise<AxiosResponse>} Informations avec clé API site
   */
  getSiteInfo: () =>
    api.get('/site/info'),

  /**
   * Teste l'authentification avec une clé API site
   * @param {string} apiKey - Clé API à tester
   * @returns {Promise<AxiosResponse>} Réponse de validation
   */
  testAuth: (apiKey) =>
    api.get('/site/test-auth', {
      headers: { 'X-Site-API-Key': apiKey }
    }),

  /**
   * Crée une nouvelle commande via l'API site
   * @param {string} apiKey - Clé API du site
   * @param {Object} orderData - Données de la commande
   * @returns {Promise<AxiosResponse>} Commande créée
   */
  createOrder: (apiKey, orderData) =>
    api.post('/site/orders', orderData, {
      headers: { 'X-Site-API-Key': apiKey }
    })
};

export const deliveryPersonsApi = {
  getAll: (filters) => api.get('/delivery-persons', { params: filters }),
  getById: (id) => api.get(`/delivery-persons/${id}`),
  create: (data) => api.post('/delivery-persons', data),
  update: (id, data) => api.put(`/delivery-persons/${id}`, data),
  delete: (id) => api.delete(`/delivery-persons/${id}`),
  regenerateToken: (id) => api.post(`/delivery-persons/${id}/regenerate-token`),
  // Suivi des colis physiquement remis au livreur
  // date (YYYY-MM-DD) optionnelle : ajoute les besoins en colis des commandes de ce jour
  getParcelSummary: (id, date) => api.get(`/delivery-persons/${id}/parcel-summary`, { params: date ? { date } : {} }),
  createParcelHandover: (id, data) => api.post(`/delivery-persons/${id}/parcel-handovers`, data),
  updateParcelHandover: (id, handoverId, data) => api.put(`/delivery-persons/${id}/parcel-handovers/${handoverId}`, data),
  // Retours en boutique des colis non livrés
  createParcelReturn: (id, data) => api.post(`/delivery-persons/${id}/parcel-returns`, data),
  updateParcelReturn: (id, returnId, data) => api.put(`/delivery-persons/${id}/parcel-returns/${returnId}`, data),
  // Zones
  getZones: (id) => api.get(`/delivery-persons/${id}/zones`),
  addZone: (id, data) => api.post(`/delivery-persons/${id}/zones`, data),
  deleteZone: (id, zoneId) => api.delete(`/delivery-persons/${id}/zones/${zoneId}`),
  // Disponibilités
  getAvailabilities: (id) => api.get(`/delivery-persons/${id}/availabilities`),
  updateAvailability: (id, data) => api.post(`/delivery-persons/${id}/availabilities`, data),
  // Paiements
  getPayments: (id, filters) => api.get(`/delivery-persons/${id}/payments`, { params: filters }),
  createPayment: (id, data) => api.post(`/delivery-persons/${id}/payments`, data),
  // Statistiques
  getStats: (id) => api.get(`/delivery-persons/${id}/stats`),
  // Livreurs disponibles
  getAvailable: (params) => api.get('/delivery-persons/available', { params }),
  // Documents
  createDocument: (deliveryId, data) => api.post(`/delivery-persons/deliveries/${deliveryId}/documents`, data),
  getDocuments: (deliveryId) => api.get(`/delivery-persons/deliveries/${deliveryId}/documents`),
  updateDocument: (documentId, data) => api.put(`/delivery-persons/documents/${documentId}`, data),
  // Obtenir les statistiques quotidiennes d'un livreur
  getDailyStats: async (deliveryPersonId, date) => {
    try {
      const response = await api.get(`/delivery-persons/stats/daily/${deliveryPersonId}/${date}`);
      return response;
    } catch (error) {
      console.error('Error getting daily stats:', error);
      return null;
    }
  },
  // Envoyer un message à un livreur
  sendMessage: async (deliveryPersonId, data) => {
    try {
      const response = await api.post(`/delivery-persons/${deliveryPersonId}/send-message`, data);
      return response;
    } catch (error) {
      console.error('Error sending message:', error);
      return null;
    }
  }
};

export const ordersApi = {
  // Récupérer toutes les commandes
  getAll: async (filters = {}) => {
    try {
      const response = await api.get('/orders', { params: filters });
      return response;
    } catch (error) {
      console.error('Error getting orders:', error);
      return null;
    }
  },

  // Récupérer une commande par son ID
  getById: async (id) => {
    try {
      const response = await api.get(`/orders/${id}`);
      return response;
    } catch (error) {
      console.error('Error getting order:', error);
      return null;
    }
  },

  // Créer une nouvelle commande
  create: async (data) => {
    try {
      const response = await api.post('/orders', data);
      return response;
    } catch (error) {
      console.error('Error creating order:', error);
      return null;
    }
  },

  // Mettre à jour une commande
  update: async (id, data) => {
    try {
      const response = await api.put(`/orders/${id}`, data);
      return response;
    } catch (error) {
      console.error('Error updating order:', error);
      return null;
    }
  },

  // Mettre à jour les items d'une commande (ajouter, modifier, supprimer des produits)
  updateOrderItems: async (id, items) => {
    try {
      const response = await api.put(`/orders/${id}/items`, { items });
      return response;
    } catch (error) {
      console.error('Error updating order items:', error);
      return null;
    }
  },

  // Réserver le stock pour une commande
  reserveStock: async (id) => {
    try {
      const response = await api.post(`/orders/${id}/reserve-stock`);
      return response;
    } catch (error) {
      console.error('Error reserving stock:', error);
      return null;
    }
  },

  // Supprimer une commande
  delete: async (id) => {
    try {
      const response = await api.delete(`/orders/${id}`);
      return response;
    } catch (error) {
      console.error('Error deleting order:', error);
      return null;
    }
  },

  // Récupérer les statistiques des commandes
  getStats: async (filters = {}) => {
    try {
      const response = await api.get('/orders/stats', { params: filters });
      return response;
    } catch (error) {
      console.error('Error getting order stats:', error);
      return null;
    }
  },

  /**
   * Récupère les compteurs du tunnel de commande storefront de la boutique active.
   *
   * Les filtres de période sont identiques à ceux des statistiques de commandes,
   * afin que les cartes du tableau de bord puissent être comparées directement.
   *
   * @param {Object} filters - Bornes optionnelles `created_at_from` et `created_at_to`.
   * @returns {Promise<Object|null>} Réponse API ou `null` si la requête échoue.
   */
  getStorefrontFunnelStats: async (filters = {}) => {
    try {
      return await api.get('/orders/storefront-funnel', { params: filters });
    } catch (error) {
      console.error('Erreur lors de la récupération du tunnel storefront:', error);
      return null;
    }
  },

  // Récupérer les commandes récentes (5 dernières)
  getRecent: async (filters = {}) => {
    try {
      const params = { ...filters, limit: 5 };
      const response = await api.get('/orders/recent', { params });
      return response;
    } catch (error) {
      console.error('Error getting recent orders:', error);
      return null;
    }
  },

  // Récupérer les zones de livraison
  getZones: async () => {
    try {
      const response = await api.get('/delivery-zones');
      return response;
    } catch (error) {
      console.error('Error getting delivery zones:', error);
      return null;
    }
  },

  // Attribuer des commandes à un livreur
  assignOrders: async (orderIds, deliveryPersonId, options = {}) => {
    try {
      const response = await api.post('/orders/assign', {
        order_ids: orderIds,
        delivery_person_id: deliveryPersonId,
        delivery_date: options.delivery_date,
        shipping_fee: options.shipping_fee,
        delivery_message_mode: options.delivery_message_mode
      });
      return response;
    } catch (error) {
      console.error('Error assigning orders:', error);
      return null;
    }
  },
  markOrderAsPaid: async (id, paymentData) => {
    try {
      const response = await api.post(`/orders/${id}/mark-as-paid`, paymentData);
      return response;
    } catch (error) {
      console.error('Error marking order as paid:', error);
      return null;
    }
  },
  cancelOrder: async (id, cancelData) => {
    try {
      const response = await api.post(`/orders/${id}/cancel`, cancelData);
      return response;
    } catch (error) {
      console.error('Error canceling order:', error);
      return null;
    }
  },

  // Rembourser une commande
  refundOrder: async (id, refundData) => {
    try {
      const response = await api.post(`/orders/${id}/refund`, refundData);
      return response;
    } catch (error) {
      console.error('Error refunding order:', error);
      return null;
    }
  },

  // Récupérer la liste des commandes pour impression
  getPrintOrdersList: async (filterType, deliveryDate = null, currentFilters = null) => {
    try {
      const params = { filter_type: filterType };

      // Si c'est un filtre par date, ajouter la date
      if (deliveryDate) {
        params.delivery_date = deliveryDate;
      }

      // Si on utilise les filtres actuels, les ajouter aux paramètres
      if (filterType === 'current_filters' && currentFilters) {
        if (currentFilters.status) {
          params.status = currentFilters.status;
        }
        if (currentFilters.customer_id) {
          params.customer_id = currentFilters.customer_id;
        }
        if (currentFilters.delivery_person_id) {
          params.delivery_person_id = currentFilters.delivery_person_id;
        }
        if (currentFilters.created_at_from && currentFilters.created_at_to) {
          params.created_at_from = currentFilters.created_at_from;
          params.created_at_to = currentFilters.created_at_to;
        }
        if (currentFilters.delivery_date_from && currentFilters.delivery_date_to) {
          params.delivery_date_from = currentFilters.delivery_date_from;
          params.delivery_date_to = currentFilters.delivery_date_to;
        }
        if (currentFilters.tags && currentFilters.tags.length > 0) {
          // Filtrer et nettoyer les tags avant envoi
          const cleanedTags = currentFilters.tags.filter(tag => tag && typeof tag === 'string' && tag.trim() !== '');
          if (cleanedTags.length > 0) {
            params.tags = cleanedTags.join(',');
          }
        }
      }

      const response = await api.get('/orders/print-list', { params });
      return response;
    } catch (error) {
      console.error('Error getting print orders list:', error);
      return null;
    }
  },

  // Récupérer les commandes contenant une variante spécifique
  /**
   * Récupère toutes les commandes contenant une variante spécifique
   * @param {string} variantId - ID de la variante à filtrer
   * @param {object} additionalFilters - Filtres additionnels optionnels (status, date, etc.)
   * @returns {Promise} - Réponse contenant les commandes filtrées
   */
  getByVariant: async (variantId, additionalFilters = {}) => {
    try {
      const params = { variant_id: variantId, ...additionalFilters };
      const response = await api.get('/orders/by-variant', { params });
      return response;
    } catch (error) {
      console.error('Error getting orders by variant:', error);
      return null;
    }
  }
};

export const createProduct = async (productData) => {
  try {
    const response = await api.post('/products', productData);
    return response.data;
  } catch (error) {
    console.error('Erreur API createProduct:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Erreur lors de la création du produit via API.');
  }
};

/**
 * Met à jour un produit existant via l'API backend.
 * @param {string} productId - L'ID du produit à mettre à jour.
 * @param {object} productData - Les nouvelles données du produit.
 * @returns {Promise<object>} La réponse de l'API.
 * @throws {Error} Si l'appel API échoue.
 */
export const updateProduct = async (productId, productData) => {
  if (!productId) throw new Error('ID du produit requis pour la mise à jour.');
  try {
    const response = await api.put(`/products/${productId}`, productData);
    return response.data; // Contient { message: '...', data: { productId: ... } }
  } catch (error) {
    console.error('Erreur API updateProduct:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Erreur lors de la mise à jour du produit via API.');
  }
};

// **NOUVELLE SECTION : API Inventaire**
export const inventoryApi = {
  /**
   * Ajuste le stock d'une variante.
   * @param {Object} data - { variantId, quantity, reason, notes?, shouldTriggerAlerts? }
   * @returns {Promise<AxiosResponse>}
   */
  adjustStock: (data) => api.post('/inventory/adjust-stock', data),

  /**
   * Configure une alerte de stock bas pour une variante.
   * @param {Object} data - { variantId, threshold, isActive, notifyUserIds? }
   * @returns {Promise<AxiosResponse>}
   */
  setupStockAlert: (data) => api.post('/inventory/alert', data),

  /**
   * Synchronise le stock d'un produit Shopify et ses variantes avec le backend.
   * @param {string} productId - ID du produit local.
   * @returns {Promise<AxiosResponse>}
   */
  syncShopifyProductStock: (productId) => api.post(`/inventory/shopify-sync/product/${productId}`),

  /**
   * Synchronise tous les produits Shopify modifiés (avec création automatique).
   * Récupère les produits modifiés depuis la dernière synchronisation et les crée/met à jour localement.
   * @returns {Promise<AxiosResponse>}
   */
  syncAllShopifyProducts: () => api.post('/inventory/shopify-sync/all'),

  /**
   * Récupère les produits avec pagination et filtres pour la gestion du stock.
   * @param {Object} params - Paramètres de requête ({ page, pageSize, searchTerm, filter }).
   * @returns {Promise<AxiosResponse<{ products: Array, totalCount: number, page: number, pageSize: number, totalPages: number }>>}
   */
  getProductsForStock: (params) => api.get('/products/products-for-stock', { params }),

  /**
   * Génère et met à jour les SKU et codes-barres pour les variantes de produits.
   * @param {Object} data - { productId, variantIds? }
   * @returns {Promise<AxiosResponse>}
   */
  generateSkuAndBarcode: (data) => api.post('/inventory/generate-codes', data),

  /**
   * Resynchronise un produit avec Shopify.
   * @param {Object} data - { productId: string }
   * @returns {Promise<AxiosResponse<any>|null>}
   */
  syncShopifyProduct: async (data) => {
    try {
      const response = await api.post('/integrations/sync-shopify-product', data);
      return response;
    } catch (error) {
      console.error('Error syncing Shopify product:', error);
      return null;
    }
  },
};

export const clientsApi = {
  /**
   * Récupère la liste des clients avec filtres (pagination, recherche).
   * @param {object} filters - Les filtres à appliquer (limit, offset, name, phone).
   * @returns {Promise<AxiosResponse<any>>} La réponse axios.
   */
  getAll: (filters = {}) => api.get('/clients', { params: filters }),

  /**
   * Récupère les détails d'un client par son ID
   * @param {string} id - L'ID du client à récupérer
   * @returns {Promise<AxiosResponse<any>>} La réponse axios.
   */
  getById: (id) => api.get(`/clients/${id}`),

  /**
   * Calcule les points de fidélité pour un client spécifique basé sur ses commandes Shopify.
   * @param {number} customerId - L'ID du client.
   * @returns {Promise<AxiosResponse<any>>} La réponse axios.
   */
  calculateShopifyPoints: (customerId) => api.post(`/clients/${customerId}/calculate-shopify-points`),

  /**
   * Calcule les points de fidélité pour un lot de clients basé sur des filtres.
   * @param {object} filters - Les filtres pour sélectionner les clients (ex: { name: 'John' }).
   * @returns {Promise<AxiosResponse<any>>} La réponse axios.
   */
  calculateShopifyPointsBatch: (filters = {}) => api.post('/clients/calculate-shopify-points/batch', { filters }),

  /**
   * Calcule les points de fidélité pour une liste spécifique d'IDs de clients.
   * @param {number[]} customerIds - Un tableau contenant les IDs des clients.
   * @returns {Promise<AxiosResponse<any>>} La réponse axios.
   */
  calculateShopifyPointsForPage: (customerIds = []) => api.post('/clients/calculate-points/page', { customerIds }),

  /**
   * Exporte les données clients au format compatible Facebook Audience.
   * @param {object} filters - Les filtres pour sélectionner les clients (ex: { name: 'John', phone: '+33...' }).
   * @returns {Promise<AxiosResponse<object>>} La réponse axios contenant l'URL de téléchargement.
   */
  exportFacebookAudience: (filters = {}) => api.post('/clients/export-facebook-audience', { filters }),
};

export const productsApi = {
  /**
   * Récupère la liste des produits.
   * @param {object} filters - Filtres éventuels (ex: pagination, recherche).
   * @returns {Promise<AxiosResponse<any>|null>}
   */
  getAll: async (filters = {}) => {
    try {
      const response = await api.get('/products', { params: filters });
      return response;
    } catch (error) {
      console.error('Error getting products:', error);
      return null;
    }
  },
  /**
 * Récupère les produits avec pagination et filtres pour la gestion du catalogue.
 * @param {Object} params - Paramètres de requête ({ page, pageSize, searchTerm, filter }).
 * @returns {Promise<AxiosResponse<{ products: Array, totalCount: number, page: number, pageSize: number, totalPages: number }>>}
 */
  getProductsForCatalog: (params) => api.get('/products/products-for-catalog', { params }),

  /**
   * Récupère un produit par son ID.
   * @param {string} id - ID du produit.
   * @returns {Promise<AxiosResponse<any>|null>}
   */
  getById: async (id) => {
    try {
      const response = await api.get(`/products/${id}`);
      return response;
    } catch (error) {
      console.error('Error getting product:', error);
      return null;
    }
  },
  /**
   * Récupère la disponibilité des fournisseurs IA pour le remplissage automatique.
   * @returns {Promise<AxiosResponse<{providers: Record<string, {available: boolean}>}>>}
   */
  getAiProviders: () => api.get('/products/ai/providers'),
  /**
   * Lit le solde de points IA disponible avant une génération d'image produit.
   * @returns {Promise<AxiosResponse<{balance: number, costs: Record<string, number>}>>}
   */
  getAiWallet: () => api.get('/products/ai/wallet'),
  /**
   * Demande des suggestions IA basées sur une image de produit.
   * @param {Object} payload - { provider: string, image: {type,data}, context?: object }
   * @returns {Promise<AxiosResponse<{ suggestions: object }>>}
   */
  requestAutoFill: (payload) => api.post('/products/ai/autofill', payload),

  /**
   * Génère des images via l'IA après préparation du prompt par GPT-5.6 Luna.
   * @param {Object} payload - { prompt, images, product, creativeOptions, model }
   * @returns {Promise<AxiosResponse<{ imageUrls: string[], prompt: string, model: string, aiBalance: number }>>}
   */
  generateImage: (payload) => api.post('/products/ai/generate-image', payload),

  /**
   * Sauvegarde une image générée choisie par l'utilisateur.
   * @param {Object} payload - { prompt, imageUrl, model }
   * @returns {Promise<AxiosResponse>}
   */
  saveGeneratedImage: (payload) => api.post('/products/ai/save-image', payload),

  /**
   * Lie au produit créé les images IA conservées temporairement sans product_id.
   * @param {{productId: string, imageIds: string[]}} payload Données de rattachement.
   * @returns {Promise<AxiosResponse<{success: boolean, data: Array}>>} Images liées.
   */
  attachGeneratedImagesToProduct: (payload) => api.post('/products/ai/images/attach', payload),

  /**
   * Supprime une image de la bibliothèque générée du produit connecté.
   * @param {string} imageId - Identifiant de l'image générée à supprimer.
   * @param {boolean} preserveFile - Garde le fichier si le formulaire actuel l'utilise encore.
   * @returns {Promise<AxiosResponse<{success: boolean}>>}
   */
  deleteGeneratedImage: (imageId, preserveFile = false) => api.delete(`/products/ai/images/${imageId}`, { data: { preserveFile } }),

  /**
   * Récupère les précisions d'image personnelles réutilisables du compte connecté.
   * @returns {Promise<AxiosResponse<{success: boolean, data: Array}>>}
   */
  getAiImagePromptPresets: () => api.get('/products/ai/image-prompt-presets'),

  /**
   * Enregistre une précision réutilisable sous un nom choisi par l'utilisateur.
   * @param {{name: string, instruction: string}} payload - Nom court et précision à sauvegarder.
   * @returns {Promise<AxiosResponse<{success: boolean, data: Object}>>}
   */
  createAiImagePromptPreset: (payload) => api.post('/products/ai/image-prompt-presets', payload),

  /**
   * Supprime une précision réutilisable appartenant au compte connecté.
   * @param {string} presetId - Identifiant de la précision à effacer.
   * @returns {Promise<AxiosResponse<{success: boolean}>>}
   */
  deleteAiImagePromptPreset: (presetId) => api.delete(`/products/ai/image-prompt-presets/${presetId}`),

  /**
   * Ajuste les prix des produits en masse selon les tags.
   * @param {Object} data - { tags, operationType, valueType, value }
   * @returns {Promise<AxiosResponse>}
   */
  adjustPrice: (data) => api.post('/products/adjust-price', data),

  /**
   * Retourne toutes les option_definitions existantes (pour l'autocomplete des noms d'options).
   * @returns {Promise<AxiosResponse<Array<{id, name, slug}>>>}
   */
  getOptionDefinitions: () => api.get('/products/option-definitions'),

  /**
   * Retourne les option_values d'une option identifiée par son slug.
   * Utilisé pour proposer les valeurs existantes lors de la saisie dans le formulaire produit.
   * @param {string} slug - Slug de l'option (ex: "couleur", "taille").
   * @returns {Promise<AxiosResponse<Array<{id, name, slug, display_hex, sort_order}>>>}
   */
  getOptionValues: (slug) => api.get(`/products/option-values/${slug}`)
};

/**
 * API pour le catalogue Facebook automatique.
 * Permet de générer une URL unique par compte que Facebook peut appeler pour récupérer le CSV.
 */
export const facebookCatalogApi = {
  /**
   * Génère (ou renouvelle) le token de catalogue et enregistre la config.
   * @param {Object} payload - { shopUrl: string, overrideFields: object }
   * @returns {Promise<AxiosResponse<{ token, feedUrl, shopUrl, overrideFields }>>}
   */
  generateToken: (payload) => api.post('/facebook-catalog/generate-token', payload),

  /**
   * Récupère la config actuelle du catalogue (token + feedUrl + shopUrl + overrides).
   * Retourne { data: null } si aucun token n'a encore été généré.
   * @returns {Promise<AxiosResponse<{ data: object|null }>>}
   */
  getTokenInfo: () => api.get('/facebook-catalog/token-info')
};



export const subscriptionApi = {
  /** Récupère les informations du plan actuel de l'utilisateur */
  getCurrentPlan: () => api.get('/subscription/current'),
  /** Récupère tous les plans disponibles */
  getAvailablePlans: () => api.get('/subscription/plans'),
  /** Récupère l'historique de facturation */
  getBillingHistory: () => api.get('/subscription/billing-history'),
  /** Récupère les statistiques d'usage (produits, boutiques, etc.) */
  getUsageStats: () => api.get('/subscription/usage-stats'),
};

export const userSettingsApi = {
  /** Récupère les paramètres utilisateur actuel */
  getCurrent: () => api.get('/user-settings'),
  /** Met à jour les paramètres utilisateur */
  update: (data) => api.put('/user-settings', data),
  /**
   * Récupère les préférences de notifications WhatsApp automatiques
   * Retourne les préférences avec les descriptions des événements
   * @returns {Promise<AxiosResponse>}
   */
  getNotificationPreferences: () => api.get('/user-settings/notification-preferences'),
  /**
   * Met à jour les préférences de notifications WhatsApp automatiques
   * @param {Object} preferences - Nouvelles préférences (whatsapp_auto_messages)
   * @returns {Promise<AxiosResponse>}
   */
  updateNotificationPreferences: (preferences) => api.put('/user-settings/notification-preferences', preferences),
};

export const bonusMessagesApi = {
  /**
   * Récupère l'historique des messages de bonus avec filtres optionnels
   * @param {Object} filters - Filtres à appliquer (customer_id, status, reduction_type, date_from, date_to, limit, offset)
   * @returns {Promise<AxiosResponse<any>>} La réponse axios
   */
  getHistory: (filters = {}) => api.get('/bonus-messages', { params: filters }),

  /**
   * Crée un nouvel enregistrement dans l'historique des messages de bonus
   * @param {Object} messageData - Données du message envoyé
   * @returns {Promise<AxiosResponse<any>>} La réponse axios
   */
  createHistory: (messageData) => api.post('/bonus-messages', messageData),

  /**
   * Met à jour le statut d'un message de bonus
   * @param {string} messageId - ID du message
   * @param {Object} statusData - Nouvelles données de statut {status, error_message}
   * @returns {Promise<AxiosResponse<any>>} La réponse axios
   */
  updateStatus: (messageId, statusData) => api.put(`/bonus-messages/${messageId}/status`, statusData),

  /**
   * Vérifie si un message de bonus a été envoyé récemment à un client
   * @param {string} customerId - ID du client
   * @param {number} hours - Nombre d'heures pour la vérification (défaut: 24)
   * @returns {Promise<AxiosResponse<any>>} La réponse axios
   */
  checkRecent: (customerId, hours = 24) => api.get(`/bonus-messages/check-recent/${customerId}`, { params: { hours } }),

  /**
   * Récupère les statistiques des messages de bonus
   * @param {Object} filters - Filtres temporels (date_from, date_to)
   * @returns {Promise<AxiosResponse<any>>} La réponse axios
   */
  getStats: (filters = {}) => api.get('/bonus-messages/stats', { params: filters }),

  /**
   * Récupère un message de bonus spécifique par ID
   * @param {string} messageId - ID du message
   * @returns {Promise<AxiosResponse<any>>} La réponse axios
   */
  getById: (messageId) => api.get(`/bonus-messages/${messageId}`)
};

// **API POUR LA GESTION DES AVIS PRODUITS**
export const reviewsApi = {
  /**
   * Créer un nouvel avis pour un produit avec images optionnelles
   * @param {Object} reviewData - Données de l'avis (productId, rating, comment, etc.)
   * @param {Array} images - Fichiers d'images (optionnel)
   * @returns {Promise<AxiosResponse>} La réponse de l'API
   */
  createReview: async (reviewData, images = []) => {
    const formData = new FormData();

    // Ajouter les données de l'avis
    Object.keys(reviewData).forEach(key => {
      if (reviewData[key] !== null && reviewData[key] !== undefined) {
        formData.append(key, reviewData[key]);
      }
    });

    // Ajouter les images si présentes
    images.forEach((image, index) => {
      formData.append('images', image);
    });

    return api.post('/reviews', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * Récupérer les avis d'un produit avec pagination et filtres
   * @param {string} productId - ID du produit
   * @param {Object} params - Paramètres (page, pageSize, rating, sortBy, sortOrder)
   * @returns {Promise<AxiosResponse>} La réponse de l'API
   */
  getProductReviews: (productId, params = {}) => {
    return api.get(`/reviews/product/${productId}`, { params });
  },

  /**
   * Obtenir les statistiques d'avis pour un produit
   * @param {string} productId - ID du produit
   * @returns {Promise<AxiosResponse>} Statistiques des avis
   */
  getReviewStats: (productId) => {
    return api.get(`/reviews/product/${productId}/stats`);
  },

  /**
   * Obtenir un avis spécifique par son ID
   * @param {string} reviewId - ID de l'avis
   * @returns {Promise<AxiosResponse>} Données de l'avis
   */
  getReviewById: (reviewId) => {
    return api.get(`/reviews/${reviewId}`);
  },

  /**
   * Mettre à jour un avis existant
   * @param {string} reviewId - ID de l'avis
   * @param {Object} updateData - Données à mettre à jour
   * @returns {Promise<AxiosResponse>} Avis mis à jour
   */
  updateReview: (reviewId, updateData) => {
    return api.put(`/reviews/${reviewId}`, updateData);
  },

  /**
   * Supprimer un avis (soft delete)
   * @param {string} reviewId - ID de l'avis
   * @returns {Promise<AxiosResponse>} Confirmation de suppression
   */
  deleteReview: (reviewId) => {
    return api.delete(`/reviews/${reviewId}`);
  },

  /**
   * Marquer un avis comme utile
   * @param {string} reviewId - ID de l'avis
   * @returns {Promise<AxiosResponse>} Avis mis à jour
   */
  markHelpful: (reviewId) => {
    return api.post(`/reviews/${reviewId}/helpful`);
  },

  /**
   * Modérer un avis (approuver/rejeter)
   * @param {string} reviewId - ID de l'avis
   * @param {boolean} isApproved - État d'approbation
   * @returns {Promise<AxiosResponse>} Avis mis à jour
   */
  moderateReview: (reviewId, isApproved) => {
    return api.put(`/reviews/${reviewId}/moderate`, { isApproved });
  },

  /**
   * Supprimer une image d'avis spécifique
   * @param {string} imageId - ID de l'image
   * @returns {Promise<AxiosResponse>} Confirmation de suppression
   */
  deleteReviewImage: (imageId) => {
    return api.delete(`/reviews/images/${imageId}`);
  },

  /**
   * Ajouter des images à un avis existant
   * @param {string} reviewId - ID de l'avis
   * @param {Array} images - Fichiers d'images à ajouter
   * @returns {Promise<AxiosResponse>} Images ajoutées
   */
  addReviewImages: (reviewId, images) => {
    const formData = new FormData();
    images.forEach(image => {
      formData.append('images', image);
    });

    return api.post(`/reviews/${reviewId}/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * Obtenir tous les avis pour tous les produits de l'utilisateur
   * @param {Object} params - Paramètres de pagination (page, pageSize)
   * @returns {Promise<AxiosResponse>} Liste paginée des avis
   */
  getAllUserReviews: (params = {}) => {
    return api.get('/reviews/user/all', { params });
  },

  /**
   * Obtenir le résumé des avis pour le dashboard
   * @returns {Promise<AxiosResponse>} Statistiques globales des avis
   */
  getDashboardSummary: () => {
    return api.get('/reviews/dashboard/summary');
  }
};

/**
 * API pour la gestion des pixels Facebook par catégorie
 * Permet de configurer des pixels différents pour chaque catégorie de produit
 */
export const facebookPixelCategoriesApi = {
  /**
   * Récupère toutes les configurations de pixels par catégorie de l'utilisateur
   * @returns {Promise<AxiosResponse>} Liste des configurations
   */
  getAll: () => api.get('/facebook-pixel-categories'),

  /**
   * Récupère la configuration d'une catégorie spécifique
   * @param {string} categoryName - Nom de la catégorie (sera encodé URL)
   * @returns {Promise<AxiosResponse>} Configuration de la catégorie
   */
  get: (categoryName) => api.get(`/facebook-pixel-categories/${encodeURIComponent(categoryName)}`),

  /**
   * Crée ou met à jour la configuration d'une catégorie
   * @param {Object} data - Données de configuration
   * @param {string} data.category_name - Nom de la catégorie (requis)
   * @param {string} data.facebook_pixel_id - ID du pixel Facebook (requis, numérique)
   * @param {string} data.facebook_access_token - Token d'accès Facebook (requis)
   * @param {string} [data.facebook_test_code] - Code de test (optionnel)
   * @param {boolean} [data.facebook_enabled=true] - Statut d'activation
   * @returns {Promise<AxiosResponse>} Configuration créée/mise à jour
   */
  create: (data) => api.post('/facebook-pixel-categories', data),

  /**
   * Supprime la configuration d'une catégorie (revient à la config globale)
   * @param {string} categoryName - Nom de la catégorie (sera encodé URL)
   * @returns {Promise<AxiosResponse>} Confirmation de suppression
   */
  delete: (categoryName) => api.delete(`/facebook-pixel-categories/${encodeURIComponent(categoryName)}`),

  /**
   * Active ou désactive la configuration d'une catégorie
   * @param {string} categoryName - Nom de la catégorie (sera encodé URL)
   * @param {boolean} enabled - Statut d'activation
   * @returns {Promise<AxiosResponse>} Configuration mise à jour
   */
  toggle: (categoryName, enabled) =>
    api.patch(`/facebook-pixel-categories/${encodeURIComponent(categoryName)}/toggle`, { enabled }),

  /**
   * Résout la configuration pixel à utiliser pour une catégorie (avec fallback sur global)
   * @param {string} categoryName - Nom de la catégorie (sera encodé URL)
   * @returns {Promise<AxiosResponse>} Configuration résolue avec source (category/global)
   */
  resolve: (categoryName) => api.get(`/facebook-pixel-categories/resolve/${encodeURIComponent(categoryName)}`),

  /**
   * Récupère la liste des catégories disponibles dans le système
   * @returns {Promise<AxiosResponse>} Liste des catégories
   */
  getAvailableCategories: () => api.get('/facebook-pixel-categories/available-categories')
};

/**
 * API pour la gestion des clients non répondants (clients bloqués)
 * Permet d'ajouter, vérifier et retirer des clients de la liste noire
 */
export const unresponsiveCustomersApi = {
  /**
   * Ajoute un client à la liste des clients non répondants
   * @param {Object} data - Données du client à bloquer
   * @param {string} data.phone - Numéro de téléphone du client (requis)
   * @param {string} data.notes - Notes/raison du blocage (optionnel)
   * @returns {Promise<AxiosResponse>} Confirmation de l'ajout
   */
  add: (data) => api.post('/unresponsive-customers', data),

  /**
   * Vérifie si un client est dans la liste des clients bloqués
   * @param {string} phone - Numéro de téléphone du client
   * @returns {Promise<AxiosResponse>} Informations du client bloqué ou null
   */
  check: (phone) => api.get(`/unresponsive-customers/check/${encodeURIComponent(phone)}`),

  /**
   * Retire un client de la liste des clients non répondants
   * @param {string} phone - Numéro de téléphone du client
   * @returns {Promise<AxiosResponse>} Confirmation de la suppression
   */
  remove: (phone) => api.delete(`/unresponsive-customers/${encodeURIComponent(phone)}`),

  /**
   * Récupère la liste de tous les clients bloqués de l'utilisateur
   * @returns {Promise<AxiosResponse>} Liste des clients bloqués
   */
  getAll: () => api.get('/unresponsive-customers')
};


/**
 * API pour la gestion des pré-commandes de produits digitaux
 * Permet de gérer les pré-commandes et marquer manuellement les paiements
 */
export const digitalProductPreordersApi = {
  /**
   * Récupère toutes les pré-commandes avec filtres optionnels
   * @param {Object} filters - Filtres (status, limit, offset)
   * @returns {Promise<AxiosResponse>} Liste des pré-commandes
   */
  getAll: (filters = {}) => api.get('/webhooks/digital-product/admin/preorders', { params: filters }),

  /**
   * Marque manuellement une pré-commande comme payée
   * Déclenche l'envoi WhatsApp et le tracking Facebook automatiquement
   * @param {string} id - ID de la pré-commande
   * @param {Object} data - Données optionnelles (amount_paid, payment_method, notes)
   * @returns {Promise<AxiosResponse>} Confirmation et résultats du traitement
   */
  markAsPaid: (id, data = {}) => api.post(`/webhooks/digital-product/admin/preorders/${id}/mark-as-paid`, data),

  /**
   * Renvoie l'email de confirmation d'achat pour une pré-commande payée
   * @param {string} id - ID de la pré-commande
   * @returns {Promise<AxiosResponse>} Résultat de l'envoi de l'email
   */
  resendEmail: (id) => api.post(`/webhooks/digital-product/admin/preorders/${id}/resend-email`)
};

/**
 * API pour la gestion des notifications de stock
 */
/**
 * API pour la gestion des templates de commandes WhatsApp
 * Gère le CRUD des templates dans la table command_types
 */
export const commandTemplatesApi = {
  /**
   * Récupère tous les templates de l'utilisateur
   * @returns {Promise<AxiosResponse>} Liste des templates
   */
  getAll: () => api.get('/command-templates'),

  /**
   * Récupère un template par son ID
   * @param {string} id - ID du template
   * @returns {Promise<AxiosResponse>} Le template
   */
  getById: (id) => api.get(`/command-templates/${id}`),

  /**
   * Crée un nouveau template
   * @param {Object} data - Données du template (code, name, message_template, parameters, active)
   * @returns {Promise<AxiosResponse>} Le template créé
   */
  create: (data) => api.post('/command-templates', data),

  /**
   * Met à jour un template existant
   * @param {string} id - ID du template
   * @param {Object} data - Données à mettre à jour
   * @returns {Promise<AxiosResponse>} Le template mis à jour
   */
  update: (id, data) => api.put(`/command-templates/${id}`, data),

  /**
   * Supprime un template
   * @param {string} id - ID du template
   * @returns {Promise<AxiosResponse>} Confirmation de suppression
   */
  delete: (id) => api.delete(`/command-templates/${id}`),

  /**
   * Crée les templates par défaut (Shopify)
   * Ne crée que les templates qui n'existent pas encore
   * @returns {Promise<AxiosResponse>} Résumé de la création
   */
  createDefaults: () => api.post('/command-templates/defaults')
};

export const stockNotificationsApi = {
  /**
   * Récupère les notifications de stock avec filtres
   * @param {Object} filters - Filtres (page, pageSize, status, productId, search)
   * @returns {Promise<AxiosResponse>} Liste des notifications
   */
  getAll: (filters = {}) => api.get('/notifications/stock', { params: filters }),

  /**
   * Marque une notification comme envoyée
   * @param {string} id - ID de la notification
   * @returns {Promise<AxiosResponse>} Notification mise à jour
   */
  markAsSent: (id) => api.put(`/notifications/stock/${id}/sent`),

  /**
   * Annule une notification
   * @param {string} id - ID de la notification
   * @returns {Promise<AxiosResponse>} Notification annulée
   */
  cancel: (id) => api.put(`/notifications/stock/${id}/cancel`)

};

/**
 * API pour la gestion des feedbacks/avis de commandes
 * Gère les avis laissés par les clients sur leurs commandes
 */
export const orderFeedbackApi = {
  /**
   * Récupère tous les feedbacks avec filtres et pagination
   * @param {Object} filters - Filtres (page, pageSize, productRating, deliveryRating, dateFrom, dateTo)
   * @returns {Promise<AxiosResponse>} Liste paginée des feedbacks
   */
  getAll: (filters = {}) => api.get('/order-feedback', { params: filters }),

  /**
   * Récupère un feedback par son ID
   * @param {string} id - ID du feedback
   * @returns {Promise<AxiosResponse>} Détails du feedback
   */
  getById: (id) => api.get(`/order-feedback/${id}`),

  /**
   * Supprime un feedback
   * @param {string} id - ID du feedback
   * @returns {Promise<AxiosResponse>} Confirmation de suppression
   */
  delete: (id) => api.delete(`/order-feedback/${id}`),

  /**
   * Récupère les statistiques des feedbacks
   * @returns {Promise<AxiosResponse>} Statistiques des feedbacks
   */
  getStats: () => api.get('/order-feedback/stats')
};

/**
 * API pour la gestion du site vitrine (storefront)
 * Permet au marchand de configurer son slug et personnaliser son site
 */
export const storefrontApi = {
  /**
   * Récupère les paramètres du site vitrine de l'utilisateur connecté
   * @returns {Promise<AxiosResponse>} Paramètres du storefront
   */
  getSettings: async () => {
    try {
      const response = await api.get('/user/storefront');
      return response.data;
    } catch (error) {
      console.error('Erreur getSettings:', error);
      throw error;
    }
  },

  /**
   * Définit ou modifie le slug (sous-domaine) du site vitrine
   * @param {string} slug - Slug à enregistrer (3-50 caractères)
   * @returns {Promise<AxiosResponse>} Slug enregistré avec URL
   */
  updateSlug: async (slug) => {
    try {
      const response = await api.put('/user/storefront/slug', { slug });
      return response.data;
    } catch (error) {
      console.error('Erreur updateSlug:', error);
      throw error;
    }
  },

  /**
   * Met à jour les paramètres de personnalisation du site vitrine
   * @param {Object} settings - Paramètres à mettre à jour
   * @param {string} settings.name - Nom de la boutique
   * @param {string} settings.logo_url - URL du logo
   * @param {string} settings.primary_color - Couleur primaire (#RRGGBB)
   * @param {string} settings.description - Description
   * @param {string} settings.whatsapp - Numéro WhatsApp
   * @param {string} settings.phone - Téléphone
   * @param {string} settings.email - Email
   * @param {string} settings.facebook_url - URL Facebook
   * @param {string} settings.instagram_url - URL Instagram
   * @returns {Promise<AxiosResponse>} Paramètres mis à jour
   */
  updateSettings: async (settings) => {
    try {
      const response = await api.put('/user/storefront/settings', settings);
      return response.data;
    } catch (error) {
      console.error('Erreur updateSettings:', error);
      throw error;
    }
  },

  /**
   * Met à jour les zones de livraison personnalisées
   * @param {Array} zones - Tableau des zones de livraison
   * @param {string} zones[].id - Identifiant unique de la zone
   * @param {string} zones[].name - Nom de la zone
   * @param {number} zones[].price - Prix de livraison
   * @param {string} zones[].description - Description optionnelle
   * @returns {Promise<AxiosResponse>} Zones mises à jour
   */
  updateDeliveryZones: async (zones) => {
    try {
      const response = await api.put('/user/storefront/delivery-zones', { delivery_zones: zones });
      return response.data;
    } catch (error) {
      console.error('Erreur updateDeliveryZones:', error);
      throw error;
    }
  }
};

/**
 * API pour la gestion des notifications Telegram admin
 * Bot centralisé côté serveur, liaison des admins, envoi de tests
 */
export const telegramApi = {
  /**
   * Récupère le statut de la config Telegram (bot configuré, liaisons admin)
   * @returns {Promise<AxiosResponse>} Config avec botConfigured, admin1Linked, admin2Linked
   */
  getConfig: () => api.get('/telegram/config'),

  /**
   * Génère un code de liaison pour associer un admin Telegram
   * @param {string} adminSlot - 'admin1' ou 'admin2'
   * @returns {Promise<AxiosResponse>} Code, instruction, botUsername
   */
  generateLinkCode: (adminSlot) => api.post('/telegram/generate-link-code', { adminSlot }),

  /**
   * Supprime la liaison Telegram d'un admin
   * @param {string} adminSlot - 'admin1' ou 'admin2'
   * @returns {Promise<AxiosResponse>} Confirmation
   */
  unlinkAdmin: (adminSlot) => api.delete('/telegram/unlink', { data: { adminSlot } }),

  /**
   * Envoie un message de test aux admins configurés
   * @param {string} adminSlot - 'admin1', 'admin2' ou 'both'
   * @returns {Promise<AxiosResponse>} Résultat du test
   */
  testNotification: (adminSlot = 'both') => api.post('/telegram/test', { adminSlot }),
};

/**
 * API de connexion au compte Meta Ads (Marketing API).
 * Le token d'accès n'est jamais renvoyé en clair : seul un aperçu masqué est exposé.
 */
export const metaAdsApi = {
  /**
   * Récupère la configuration Meta Ads de l'utilisateur.
   * @returns {Promise<AxiosResponse<{ data: object|null }>>} Config (token masqué) ou null
   */
  getSettings: () => api.get('/meta-ads/settings'),

  /**
   * Demande l'URL de connexion OAuth officielle de Meta pour l'utilisateur connecté.
   * Le navigateur est ensuite redirigé vers Meta : aucun mot de passe Meta ne passe
   * par le frontend ni par l'API Wabot.
   * @returns {Promise<AxiosResponse<{ data: { authorizationUrl: string } }>>} URL Meta signée
   */
  getOAuthAuthorizationUrl: () => api.get('/meta-ads/oauth/authorize'),

  /**
   * Enregistre ou met à jour la configuration Meta Ads.
   * Le token est optionnel : s'il est omis, celui déjà enregistré est conservé.
   * @param {Object} payload - { meta_ads_access_token?, meta_ads_account_id, meta_ads_page_id, ... }
   * @returns {Promise<AxiosResponse>} Configuration enregistrée
   */
  saveSettings: (payload) => api.post('/meta-ads/settings', payload),

  /**
   * Teste la connexion Meta et liste les comptes publicitaires, Pages et catalogues accessibles.
   * @param {Object} [payload] - { accessToken?, adAccountId? } pour tester avant enregistrement
   * @returns {Promise<AxiosResponse>} { adAccounts, pages, catalogs, account }
   */
  verify: (payload = {}) => api.post('/meta-ads/verify', payload),

  /**
   * Récupère les objectifs publicitaires et leur disponibilité selon la configuration.
   * @returns {Promise<AxiosResponse>} Liste { key, label, available, missingSettings, ... }
   */
  getObjectives: () => api.get('/meta-ads/objectives'),

  /**
   * Liste les formulaires instantanés liés à la Page choisie pour la campagne.
   * @param {string} pageId - Identifiant de la Page Facebook concernée.
   * @returns {Promise<AxiosResponse>} Formulaires Meta disponibles.
   */
  getLeadForms: (pageId) => api.get('/meta-ads/lead-forms', { params: { pageId } }),

  /**
   * Crée un formulaire prospect directement dans Meta puis renvoie son identifiant.
   * @param {Object} payload - Configuration validée du formulaire instantané.
   * @returns {Promise<AxiosResponse>} Formulaire créé sur la Page Meta.
   */
  createLeadForm: (payload) => api.post('/meta-ads/lead-forms', payload),

  /** Génère la stratégie à valider avant les créas et le brouillon final. */
  generateStrategy: (payload) => api.post('/meta-ads/strategy', payload),

  /**
   * Récupère les ressources Meta accessibles avec le token enregistré (Pages, pixels, catalogues).
   * Renvoie des listes vides plutôt qu'une erreur si la configuration est incomplète.
   * @returns {Promise<AxiosResponse>} { adAccounts, pages, pixels, catalogs, account }
   */
  getAssets: () => api.get('/meta-ads/assets'),

  /**
   * Recherche des lieux ciblables dans le référentiel Meta (villes, régions, pays).
   * @param {string} q - Texte recherché (minimum 2 caractères)
   * @param {string} [type] - 'city', 'region' ou 'country'
   * @returns {Promise<AxiosResponse>} Liste { key, name, region, country }
   */
  searchGeoLocations: (q, type = 'city') => api.get('/meta-ads/geo-search', { params: { q, type } }),

  /**
   * Liste les audiences du compte publicitaire (personnalisées et similaires).
   * @returns {Promise<AxiosResponse>} Liste { id, name, subtype, taille, pretePourDiffusion }
   */
  getAudiences: () => api.get('/meta-ads/audiences'),

  /**
   * Crée une audience personnalisée à partir des clients du backoffice.
   * Les emails et téléphones sont hachés côté serveur avant envoi à Meta.
   * @param {Object} payload - { name?, filters? }
   * @returns {Promise<AxiosResponse>} { id, name, clientsEnvoyes }
   */
  createAudienceFromCustomers: (payload = {}) => api.post('/meta-ads/audiences/from-customers', payload),

  /**
   * Crée une audience similaire (lookalike) à partir d'une audience source.
   * @param {Object} payload - { sourceAudienceId, country, ratio?, name? }
   * @returns {Promise<AxiosResponse>} Audience créée
   */
  createLookalikeAudience: (payload) => api.post('/meta-ads/audiences/lookalike', payload),

  /**
   * Génère un brouillon de campagne avec l'IA (aucun envoi vers Meta à cette étape).
   * @param {Object} payload - { objective, productIds, dailyBudget, durationDays, countries, notes }
   * @returns {Promise<AxiosResponse>} Brouillon { campaign, adSets, warnings, ... }
   */
  generateDraft: (payload) => api.post('/meta-ads/draft', payload),

  /** Enregistre ou met à jour une étape de brouillon, sans solliciter l'IA. */
  saveCampaignDraft: (payload) => api.post('/meta-ads/drafts', payload),

  /** Liste les brouillons Meta Ads disponibles pour reprise. */
  getCampaignDrafts: () => api.get('/meta-ads/drafts'),

  /** Charge le contenu d'un brouillon précis pour reprendre son parcours. */
  getCampaignDraft: (id) => api.get(`/meta-ads/drafts/${id}`),

  /** Supprime un brouillon non publié. */
  deleteCampaignDraft: (id) => api.delete(`/meta-ads/drafts/${id}`),

  /**
   * Rédige le prompt de génération d'image d'une annonce (appel IA dédié).
   * Séparé de la génération de campagne pour ne pas diluer l'attention du modèle.
   * @param {Object} payload - { ad, product?, visualBrief?, countries?, count? }
   * @returns {Promise<AxiosResponse>} { prompts: [{ prompt, focus }] }
   */
  generateImagePrompt: (payload) => api.post('/meta-ads/image-prompt', payload),

  /**
   * Crée la campagne sur Meta à partir du brouillon validé. Tout est créé en statut PAUSE.
   * @param {Object} draft - Brouillon validé (éventuellement édité par l'utilisateur)
   * @returns {Promise<AxiosResponse>} Campagne enregistrée
   */
  publish: (draft) => api.post('/meta-ads/publish', { draft }),

  /**
   * Retourne l'historique des campagnes créées depuis le backoffice.
   * @returns {Promise<AxiosResponse>} Liste des campagnes
   */
  getCampaigns: () => api.get('/meta-ads/campaigns'),

  /**
   * Récupère les performances d'une campagne (dépense, impressions, clics, résultats).
   * @param {string} id - Identifiant interne de la campagne
   * @returns {Promise<AxiosResponse>} Statistiques
   */
  getCampaignInsights: (id, period = {}) => api.get(`/meta-ads/campaigns/${id}/insights`, { params: period }),

  /** Charge les performances d'un ensemble enregistré dans une campagne Wabot. */
  getAdSetInsights: (campaignId, adSetId, period = {}) => api.get(`/meta-ads/campaigns/${campaignId}/adsets/${adSetId}/insights`, { params: period }),

  /** Charge les performances d'une publicité enregistrée dans une campagne Wabot. */
  getAdInsights: (campaignId, adId, period = {}) => api.get(`/meta-ads/campaigns/${campaignId}/ads/${adId}/insights`, { params: period }),

  /** Charge les médias d'aperçu du créatif d'une publicité enregistrée dans Wabot. */
  getAdPreview: (campaignId, adId) => api.get(`/meta-ads/campaigns/${campaignId}/ads/${adId}/preview`),

  /**
   * Demande des recommandations IA consultatives pour une campagne et une période.
   * Aucune modification de la campagne n'est réalisée par cet appel.
   */
  getCampaignRecommendations: (id, period = {}) => api.get(`/meta-ads/campaigns/${id}/recommendations`, { params: period }),

  /**
   * Charge les commandes de la période proposées pour une attribution manuelle.
   * Les données personnelles des clients ne font pas partie de cette réponse.
   */
  getCampaignRecommendationOrders: (id, period = {}) => api.get(`/meta-ads/campaigns/${id}/recommendation-orders`, { params: period }),

  /**
   * Lance l'analyse avec les commandes explicitement rattachées par l'utilisateur.
   * Le serveur relit ces identifiants afin de contrôler leur appartenance au compte.
   */
  analyzeCampaignWithOrders: (id, payload = {}) => api.post(`/meta-ads/campaigns/${id}/recommendations`, payload),

  /**
   * Charge les indicateurs consolidés, alertes et classements Meta Ads.
   * @param {Object} period - { preset? } ou { since, until }
   * @returns {Promise<AxiosResponse>} Tableau de bord de performances
   */
  getDashboard: (period = {}) => api.get('/meta-ads/dashboard', { params: period }),

  /** Retourne l'historique et le coût estimé des requêtes IA Meta Ads. */
  getAIUsage: () => api.get('/meta-ads/ai-usage'),

  /**
   * Active ou met en pause une campagne sur Meta.
   * @param {string} id - Identifiant interne de la campagne
   * @param {string} status - 'ACTIVE' ou 'PAUSED'
   * @returns {Promise<AxiosResponse>} Campagne mise à jour
   */
  updateCampaignStatus: (id, status) => api.post(`/meta-ads/campaigns/${id}/status`, { status }),
};

export default api;
